import { useEffect } from 'react';
import Footer from '../../home/components/Footer';

export default function BasementFinishingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://americandreamremodelinc.com';

    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Basement Finishing',
      description: 'Professional basement finishing in Southeast Michigan — waterproofing, entertainment rooms, guest suites, home offices, and full electrical & HVAC by American Dream Remodeling INC.',
      provider: {
        '@type': 'LocalBusiness',
        name: 'American Dream Remodeling INC',
        url: siteUrl,
        telephone: '+1-586-443-0294',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Detroit',
          addressRegion: 'MI',
          addressCountry: 'US',
        },
      },
      areaServed: [
        { '@type': 'County', name: 'Macomb County' },
        { '@type': 'County', name: 'Oakland County' },
        { '@type': 'County', name: 'Wayne County' },
        { '@type': 'County', name: 'Washtenaw County' },
      ],
      url: `${siteUrl}/services/basement-finishing`,
    };

    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/#services` },
        { '@type': 'ListItem', position: 3, name: 'Basement Finishing', item: `${siteUrl}/services/basement-finishing` },
      ],
    };

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How much does basement finishing cost in Southeast Michigan?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Basement finishing costs vary based on square footage, layout complexity, and finishes selected. American Dream Remodeling INC offers free, no-obligation estimates for all basement finishing projects in Macomb, Oakland, Wayne, and Washtenaw County.',
          },
        },
        {
          '@type': 'Question',
          name: 'How long does it take to finish a basement?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Most basement finishing projects take 3–6 weeks depending on the scope of work. Projects with full bathrooms or complex layouts may take longer. We provide a detailed timeline during your free consultation.',
          },
        },
        {
          '@type': 'Question',
          name: 'Do you handle waterproofing before finishing a basement?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, American Dream Remodeling INC evaluates and addresses moisture control and waterproofing before beginning any basement finishing work to ensure a dry, long-lasting living space.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can a finished basement include a full bathroom?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Absolutely. We can add a full bathroom, half bath, or wet bar to your finished basement. We handle all plumbing, electrical, and finishing work as part of the project.',
          },
        },
      ],
    };

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Basement Finishing in Southeast Michigan | American Dream Remodeling INC',
      description: 'Professional basement finishing in Southeast Michigan — waterproofing, entertainment rooms, guest suites, home offices, and full electrical & HVAC by American Dream Remodeling INC.',
      url: `${siteUrl}/services/basement-finishing`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/#services` },
          { '@type': 'ListItem', position: 3, name: 'Basement Finishing', item: `${siteUrl}/services/basement-finishing` },
        ],
      },
    };

    const injectSchema = (id: string, data: object) => {
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement('script');
        el.id = id;
        (el as HTMLScriptElement).type = 'application/ld+json';
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(data);
    };

    injectSchema('schema-basement-service', serviceSchema);
    injectSchema('schema-basement-breadcrumb', breadcrumb);
    injectSchema('schema-basement-faq', faqSchema);
    injectSchema('schema-basement-webpage', webPageSchema);

    return () => {
      ['schema-basement-service', 'schema-basement-breadcrumb', 'schema-basement-faq', 'schema-basement-webpage'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  const scrollToEstimate = () => {
    sessionStorage.setItem('scrollToEstimate', 'true');
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/a79375986a1b6a7b511fad48fd69914b.jpg"
            alt="Basement Finishing Services"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Basement Finishing</h1>
          <p className="text-xl md:text-2xl mb-8">Turn your unfinished basement into valuable living space - from entertainment rooms to home offices and guest suites.</p>
          <button
            onClick={scrollToEstimate}
            className="bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-600 transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            Get Free Estimate
          </button>
        </div>
      </section>

      {/* Service Details */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Unlock Your Basement's Potential</h2>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Your basement represents hundreds of square feet of untapped potential. Whether you need extra living space, a home theater, guest suite, home office, or playroom, finishing your basement is a cost-effective way to add value and functionality to your home.
              </p>
              <p className="text-gray-600 leading-relaxed">
                American Dream Remodeling INC specializes in transforming dark, unused basements into beautiful, comfortable living spaces throughout Southeast Michigan. We handle everything from moisture control to final finishes.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=modern%20basement%20home%20theater%20with%20comfortable%20seating%20large%20screen%20ambient%20lighting%20dark%20walls%20clean%20simple%20background%20professional%20interior%20photography&width=800&height=600&seq=basement-detail-001&orientation=landscape"
                alt="Finished Basement Entertainment Room"
                className="w-full h-full object-cover rounded-xl shadow-lg"
              />
            </div>
          </div>

          {/* What We Offer */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What We Offer</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: 'ri-water-percent-line', title: 'Waterproofing', desc: 'Moisture control and prevention systems' },
                { icon: 'ri-layout-4-line', title: 'Space Planning', desc: 'Maximize your basement layout' },
                { icon: 'ri-tv-2-line', title: 'Entertainment Rooms', desc: 'Home theaters and game rooms' },
                { icon: 'ri-hotel-bed-line', title: 'Guest Suites', desc: 'Bedrooms with full bathrooms' },
                { icon: 'ri-briefcase-line', title: 'Home Offices', desc: 'Professional workspace solutions' },
                { icon: 'ri-plug-line', title: 'Electrical & HVAC', desc: 'Proper climate control and power' },
              ].map((item, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-500 text-white rounded-lg mb-4">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Process */}
          <div className="bg-gradient-to-br from-gray-50 to-white p-12 rounded-2xl border border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: '1', title: 'Assessment', desc: 'Evaluate your basement and discuss goals' },
                { step: '2', title: 'Design', desc: 'Create a functional layout plan' },
                { step: '3', title: 'Build', desc: 'Professional construction and finishing' },
                { step: '4', title: 'Enjoy', desc: 'Your new living space is ready' },
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-teal-500 text-white rounded-full text-2xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Finish Your Basement?</h2>
          <p className="text-xl text-white/90 mb-8">Get a free, no-obligation estimate for your basement finishing project in Southeast Michigan.</p>
          <button
            onClick={scrollToEstimate}
            className="bg-white text-teal-600 px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            Get Your Free Estimate
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}